import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import { isAxiosError } from "axios";
import { Building2, Gauge, Loader2, Search } from "lucide-react";

import {
  useEstateSummaries,
  type EstateSummary,
} from "@/hooks/use-estate-summaries";
import { useAiDeflectionMap } from "@/hooks/use-ai-deflection-map";
import { useCellScope } from "@/hooks/use-cell-scope";
import { CellSelect } from "@/components/cells/cell-select";
import { CellScopeAlerts } from "@/components/cells/cell-scope-alerts";
import { CellFailuresAlert } from "@/components/cells/cell-failures-alert";
import { isCellSignInError } from "@/lib/cells/cell-session";
import { CELLS, PRIMARY_CELL, findCell, type Cell } from "@/lib/cells/cells";
import { Page, PageHeader, PageTitle } from "@/components/common/page";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const searchSchema = z.object({
  tenant_id: z.string().default(""),
  cell: z.string().default("all"),
});

export const Route = createFileRoute("/agent/ai-deflection-map")({
  validateSearch: zodValidator(searchSchema),
  component: AiDeflectionMapPage,
});

function DetailEmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-12 text-center">
      <Gauge className="text-muted-foreground size-8" />
      <p className="text-sm font-medium">{title}</p>
      <p className="text-muted-foreground max-w-sm text-sm">{description}</p>
    </div>
  );
}

function DeflectionPill({ rate }: { rate: number | null }) {
  if (rate === null) {
    return <span className="text-muted-foreground/60 text-xs">—</span>;
  }
  const tone =
    rate >= 60
      ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
      : rate >= 45
        ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
        : "bg-muted text-muted-foreground";
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
        tone,
      )}
    >
      {rate.toFixed(0)}%
    </span>
  );
}

function tenantLabel(s: EstateSummary & { cell?: Cell }): string {
  const name = s.company ?? s.tenant_id.slice(0, 8);
  return CELLS.length > 1 && s.cell ? `${name} (${s.cell.residency})` : name;
}

function AiDeflectionMapPage() {
  const { tenant_id, cell } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const cellFilter = CELLS.length > 1 ? (findCell(cell)?.id ?? "all") : "all";
  // The roster follows the cell choice; a selected tenant's report its own cell.
  const scope = useCellScope({ cellFilter });
  const {
    rows: summaries,
    failures,
    loadingCells,
    isLoading: summariesLoading,
  } = useEstateSummaries(scope.queriedCells, scope.tenants);
  const listLoading = summariesLoading || scope.queriedCells.length === 0;
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return summaries;
    return summaries.filter((s) => tenantLabel(s).toLowerCase().includes(q));
  }, [summaries, query]);

  const hasTenant = tenant_id !== "";
  const selectTenant = (id: string) => {
    void navigate({ search: { tenant_id: id, cell } });
  };
  const handleCellChange = (value: string) => {
    void navigate({ search: { tenant_id: "", cell: value } });
  };

  // The report comes from the selected tenant's own cell.
  const tenantCell = scope.showCells
    ? (summaries.find((s) => s.tenant_id === tenant_id)?.cell ??
      findCell(scope.tenants?.find((t) => t.id === tenant_id)?.deployment_cell))
    : PRIMARY_CELL;
  const {
    data: html,
    isLoading,
    isError,
    error,
  } = useAiDeflectionMap(hasTenant ? tenant_id : undefined, tenantCell);
  const loadedCells = scope.queriedCells.filter(
    (c) =>
      !failures.some((f) => f.cell.id === c.id) &&
      !loadingCells.some((l) => l.id === c.id),
  );

  const notFound =
    isError && isAxiosError(error) && error.response?.status === 404;
  // The tenant's cell isn't known yet, or can't be: a list that would say failed.
  const tenantCellPending =
    hasTenant && !tenantCell && (listLoading || scope.tenantsLoading);
  const tenantCellUnknown = hasTenant && !tenantCell && !tenantCellPending;
  const cellFailuresForTenant = [...scope.tenantFailures, ...failures];

  return (
    <Page className="flex h-full flex-col">
      <PageHeader>
        <PageTitle>AI Deflection</PageTitle>
      </PageHeader>
      {scope.showCells && (
        <div className="mt-2 flex flex-col gap-2">
          <CellSelect
            value={hasTenant && tenantCell ? tenantCell.id : cellFilter}
            onChange={handleCellChange}
          />
          <CellScopeAlerts
            scope={scope}
            failures={failures}
            loadedCells={loadedCells}
            isLoading={listLoading}
          />
        </div>
      )}

      <div className="mt-2 grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[340px_1fr]">
        {/* Tenant list */}
        <div className="bg-card flex min-h-0 flex-col rounded-xl border">
          <div className="border-b p-2">
            <div className="relative">
              <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2" />
              <Input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
                placeholder="Search tenants…"
                className="pl-8"
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {listLoading ? (
              <div className="text-muted-foreground flex items-center justify-center gap-2 p-6 text-sm">
                <Loader2 className="size-4 animate-spin" />
                Loading tenants…
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-muted-foreground p-4 text-center text-sm">
                {summaries.length === 0
                  ? "No reports generated yet."
                  : "No tenant found."}
              </p>
            ) : (
              <ul className="flex flex-col gap-0.5">
                {filtered.map((s) => {
                  const selected = s.tenant_id === tenant_id;
                  return (
                    <li key={s.tenant_id}>
                      <button
                        type="button"
                        onClick={() => {
                          selectTenant(s.tenant_id);
                        }}
                        aria-current={selected}
                        className={cn(
                          "focus-visible:ring-ring flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2",
                          selected
                            ? "bg-accent text-accent-foreground ring-primary/40 font-medium ring-1"
                            : "hover:bg-accent/60",
                        )}
                      >
                        <span className="bg-background flex size-7 shrink-0 items-center justify-center rounded-md border">
                          <Building2 className="text-muted-foreground size-3.5" />
                        </span>
                        <span className="truncate">{tenantLabel(s)}</span>
                        <span className="ml-auto flex shrink-0 items-center gap-2 pl-2">
                          {s.conversations !== null && (
                            <span className="text-muted-foreground text-xs tabular-nums">
                              {s.conversations.toLocaleString()}
                            </span>
                          )}
                          <DeflectionPill rate={s.deflection_rate} />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Report detail */}
        <div className="bg-card min-h-0 overflow-hidden rounded-xl border">
          {!hasTenant ? (
            <DetailEmptyState
              title="Select a tenant"
              description="Choose a tenant from the list to view its AI deflection estate report."
            />
          ) : isLoading || tenantCellPending ? (
            <div className="text-muted-foreground flex h-full items-center justify-center gap-2 text-sm">
              <Loader2 className="size-4 animate-spin" />
              Loading report…
            </div>
          ) : tenantCellUnknown ? (
            cellFailuresForTenant.length > 0 ? (
              <div className="p-6">
                <CellFailuresAlert
                  failures={cellFailuresForTenant}
                  heading={(cell) =>
                    `Can't tell which cell this tenant is in: ${cell.residency} didn't load`
                  }
                  signInInNewTab
                />
              </div>
            ) : (
              <DetailEmptyState
                title="Tenant not found"
                description="This tenant isn't in any cell's tenant list."
              />
            )
          ) : isError && isCellSignInError(error) && tenantCell ? (
            <div className="p-6">
              <CellFailuresAlert
                failures={[{ cell: tenantCell, error: error }]}
                heading={(cell) => `Sign in to ${cell.id} to see this report`}
                signInInNewTab
              />
            </div>
          ) : notFound ? (
            <DetailEmptyState
              title="No report yet"
              description="No estate report has been generated for this tenant yet. Reports are produced on a weekly schedule."
            />
          ) : isError ? (
            <DetailEmptyState
              title="Failed to load report"
              description="Something went wrong fetching the estate report. Please try again."
            />
          ) : (
            <iframe
              title="AI Deflection"
              srcDoc={html}
              sandbox="allow-scripts"
              className="size-full bg-white"
            />
          )}
        </div>
      </div>
    </Page>
  );
}
